import React, { useState, useRef, useEffect, useMemo } from 'react';
import _ from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import ReactMinimalPieChart from 'react-minimal-pie-chart';

import { totalSegments, usePrevious } from './helpers';

const DonutChartComponent = props => {
  const {
    startAngle,
    className,
    isLegend,
    size,
    radius,
    dataSections,
    thickness,
    onClick,
    loading,
  } = props;
  const [isLoaded, setIsLoaded] = useState(false);
  const timeoutRef = useRef(null);

  const prevDataSections = usePrevious(dataSections);
  const total = useMemo(() => totalSegments(dataSections), [dataSections, loading]);
  const sectionValues = useMemo(() => _.map(dataSections, 'value'), [dataSections, loading]);

  timeoutRef.current = setTimeout(() => setIsLoaded(true), 100);

  useEffect(() => {
    if (!_.isEqual(prevDataSections, dataSections)) {
      setIsLoaded(false);
      timeoutRef.current = setTimeout(() => {
        setIsLoaded(true);
      }, 100);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [...sectionValues]);

  const reversedSegments = useMemo(() => _.reverse(_.cloneDeep(dataSections)), [...sectionValues]);
  const onClickFunc = segment => () => onClick(segment);

  const renderLegend = useMemo(
    () => (
      <div className={`${className}__legend`}>
        {_.map(reversedSegments, (segment, i) => (
          <div className={`${className}__legend-option`} key={i}>
            <div className={`${className}__legend-text-wrapper`}>
              <div
                className={`${className}__legend-dot`}
                style={{ backgroundColor: segment.color }}
              />
              {segment.text}
            </div>
            <div className={`${className}__legend-value`}>{segment.value}</div>
          </div>
        ))}
      </div>
    ),
    [reversedSegments],
  );

  const renderChart = useMemo(() => {
    const segmentsAreEmpty = total === 0;
    const chartProps = {
      className: `${className}__circle`,
      cx: radius,
      cy: radius,
      lengthAngle: -360,
      lineWidth: 20,
      paddingAngle: 0,
      radius,
      rounded: true,
      startAngle,
      viewBoxSize: [size, size],
    };
    const emptyData = [{ value: 100, color: '#626B70' }];
    return (
      <>
        {segmentsAreEmpty ? (
          <ReactMinimalPieChart {...chartProps} data={emptyData} />
        ) : (
          <ReactMinimalPieChart {...chartProps} data={dataSections} onClick={onClickFunc} />
        )}

        <p className={`${className}__circle-text`}>{total}</p>
      </>
    );
  }, [size, radius, dataSections, total, thickness, isLoaded]);

  return (
    <div className={`${className}__chart-wrapper`}>
      <div
        className={classnames(className, className, {
          'donut-chart--loaded': isLoaded,
        })}
      >
        {renderChart}
      </div>

      {isLegend && renderLegend}
    </div>
  );
};

DonutChartComponent.propTypes = {
  className: PropTypes.string,
  dataSections: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      text: PropTypes.string,
      value: PropTypes.number,
    }),
  ),
  isLegend: PropTypes.bool,
  legend: PropTypes.shape({
    render: PropTypes.bool,
    title: PropTypes.string,
  }),
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  radius: PropTypes.number,
  size: PropTypes.number,
  startAngle: PropTypes.number,
  thickness: PropTypes.number,
};

export default DonutChartComponent;
